import { z } from 'zod';
import { allUserRoleSchema } from '../data.types.js';
import {
  BaseResponseSchema,
  companyAliasSchema,
  companyNameSchema,
  dateSchema,
  emailSchema,
  firstNameSchema,
  integerIdSchema,
  lastNameSchema,
  phoneNumberSchema,
  uuidSchema,
} from './primitives.js';

export const GROUP_NAME_MAX_LENGTH = 100
export const GROUP_DESCRIPTION_MAX_LENGTH = 500

const groupNameSchema = z.string().min(1).max(GROUP_NAME_MAX_LENGTH).trim();
const groupDescriptionSchema = z.string().max(GROUP_DESCRIPTION_MAX_LENGTH).trim();


// Tenant User Management Schemas
export const CreateTenantUserRequestSchema = z.object({
  email: emailSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  phone: phoneNumberSchema.optional(),
  role: allUserRoleSchema,
  groupIds: z.array(integerIdSchema).optional(),
  sendInvitation: z.boolean().optional()
});

export const UpdateTenantUserRequestSchema = z.object({
  firstName: firstNameSchema.optional(),
  lastName: lastNameSchema.optional(),
  phone: phoneNumberSchema.optional(),
  role: allUserRoleSchema.optional(),
  groupIds: z.array(integerIdSchema).optional(),
  isActive: z.boolean().optional()
});

export function coerceTenantUsersIsActiveQuery(val: unknown): unknown {
  if (val === 'true' || val === '1' || val === 1 || val === true) return true;
  if (val === 'false' || val === '0' || val === 0 || val === false) return false;
  return val;
}

export const GetTenantUsersRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  role: allUserRoleSchema.optional(),
  groupId: integerIdSchema.optional(),
  isActive: z.preprocess(coerceTenantUsersIsActiveQuery, z.boolean().optional()),
  sortBy: z.enum(['email', 'firstName', 'lastName', 'createdAt', 'lastLoginAt']).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional()
});

export const ResendProviderInvitationResponseSchema = z.object({
  tempPassword: z.string(),
  email: emailSchema
});

// Self-Service Profile Update Schemas — see ./schemas/auth.schemas.ts

// Group Management Schemas
export const CreateGroupRequestSchema = z.object({
  name: groupNameSchema,
  description: groupDescriptionSchema.optional()
});

export const UpdateGroupRequestSchema = z.object({
  name: groupNameSchema.optional(),
  description: groupDescriptionSchema.optional()
});

export const GetGroupsRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  sortBy: z.enum(['name', 'createdAt', 'userCount']).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional()
});

// Customer Management Schemas
export const CreateCustomerRequestSchema = z.object({
  companyName: companyNameSchema,
  companyAlias: companyAliasSchema,
  companyAddress: z.string().max(500).trim().optional(),
  companyEmail: emailSchema.optional(),
  companyPhone: phoneNumberSchema.optional()
});

export const UpdateCustomerRequestSchema = z.object({
  companyName: companyNameSchema.optional(),
  companyAlias: companyAliasSchema.optional(),
  companyAddress: z.string().max(500).trim().optional(),
  companyEmail: emailSchema.optional(),
  companyPhone: phoneNumberSchema.optional()
});

export const GetCustomersRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  sortBy: z.enum(['name', 'alias', 'createdAt']).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional()
});



export const GroupSummarySchema = z.object({
  groupId: integerIdSchema,
  groupName: groupNameSchema,
});

export const UserSummarySchema = z.object({
  id: integerIdSchema,
  email: emailSchema.optional(),
  firstName: firstNameSchema.optional(),
  lastName: lastNameSchema.optional(),
});

export const UserResponseSchema = z.object({
  id: integerIdSchema,
  email: emailSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  phone: phoneNumberSchema.optional(),
  role: allUserRoleSchema,
  isActive: z.coerce.boolean(),
  groups: z.array(GroupSummarySchema),
  lastLoginAt: dateSchema.optional().nullable(),
  passwordChangedAt: dateSchema.optional().nullable(),
  createdAt: dateSchema,
  createdBy: UserSummarySchema.optional().nullable(),
});

export const GetUserResponseSchema = z.object({
  users: z.array(UserResponseSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(0),
  limit: z.number().int().min(0),
  totalPages: z.number().int().min(0)
});

export const CustomerResponseSchema = z.object({
  id: integerIdSchema,
  companyName: companyNameSchema,
  companyAlias: companyAliasSchema,
  companyAddress: z.string().max(200).trim().optional().nullable(),
  companyEmail: emailSchema.optional(),
  companyPhone: phoneNumberSchema.optional(),
  waferCount: z.number().int().min(0),// Number of wafers associated with this customer
  containerCount: z.number().int().min(0), // Number of containers associated with this customer
  createdAt: dateSchema,
  updatedAt: dateSchema,
  createdBy: UserSummarySchema.optional()
});

export const GetCustomerResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    customers: z.array(CustomerResponseSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(0),
    limit: z.number().int().min(0),
    totalPages: z.number().int().min(0)
  })
});



export const CreateCustomerResponseSchema = BaseResponseSchema.extend({
  data: CustomerResponseSchema
});

export const GetCustomerByIdResponseSchema = BaseResponseSchema.extend({
  data: CustomerResponseSchema
});

export const UpdateCustomerResponseSchema = BaseResponseSchema.extend({
  data: CustomerResponseSchema
});

// Contact directory (custodian / owner PII)
export const CreateContactRequestSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  phone: phoneNumberSchema.optional(),
  customerId: integerIdSchema.optional(),
  linkedUserId: integerIdSchema.optional(),
});

export const UpdateContactRequestSchema = z.object({
  firstName: firstNameSchema.optional(),
  lastName: lastNameSchema.optional(),
  email: emailSchema.optional(),
  phone: phoneNumberSchema.optional().nullable(),
  customerId: integerIdSchema.optional().nullable(),
});

export const GetContactsRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  status: z.enum(['active', 'archived']).optional().default('active'),
});

export const ContactSearchQuerySchema = z.object({
  q: z.string().min(1).max(200).trim(),
  limit: z.coerce.number().int().min(1).max(50).optional().default(20),
});

export const ContactIdParamSchema = z.object({
  contactId: integerIdSchema,
});

export const ContactResponseSchema = z.object({
  id: integerIdSchema,
  versionId: z.string().min(1),
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  phone: phoneNumberSchema.optional(),
  customerId: integerIdSchema.optional(),
  linkedUserId: integerIdSchema.optional(),
  isArchived: z.boolean().optional(),
  waferLinkCount: z.number().int().min(0).optional(),
  containerLinkCount: z.number().int().min(0).optional(),
  version: z.number().int().min(1).optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const ContactSearchResultSchema = z.object({
  contactId: integerIdSchema.nullable(),
  versionId: z.string().nullable(),
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  phone: phoneNumberSchema.optional(),
  source: z.enum(['contact', 'user']),
  userId: integerIdSchema.optional(),
});

export const GetContactsResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    contacts: z.array(ContactResponseSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(0),
    limit: z.number().int().min(0),
    totalPages: z.number().int().min(0),
  }),
});

export const ContactSearchResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    results: z.array(ContactSearchResultSchema),
  }),
});

export const CreateContactResponseSchema = BaseResponseSchema.extend({
  data: ContactResponseSchema,
});

export const GetContactByIdResponseSchema = BaseResponseSchema.extend({
  data: ContactResponseSchema,
});

export const ContactVersionHistoryItemSchema = z.object({
  versionId: z.string().min(1),
  version: z.number().int().min(1),
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  phone: phoneNumberSchema.nullable().optional(),
  createdAt: dateSchema,
  supersededAt: dateSchema.nullable().optional(),
  isCurrent: z.boolean(),
});

export const ContactDetailResponseSchema = ContactResponseSchema.extend({
  versions: z.array(ContactVersionHistoryItemSchema),
});

export const GetContactDetailResponseSchema = BaseResponseSchema.extend({
  data: ContactDetailResponseSchema,
});

export const UpdateContactResponseSchema = BaseResponseSchema.extend({
  data: ContactResponseSchema,
});


export const GroupResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    groupId: integerIdSchema,
    groupName: groupNameSchema,
    description: groupDescriptionSchema.optional().nullable(),
    userCount: z.number().int().min(0),
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
    createdBy: UserSummarySchema.optional(),
  })
});

export const GetGroupResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    groups: z.array(GroupResponseSchema.shape.data),
    total: z.number().int().min(0),
    page: z.number().int().min(0),
    limit: z.number().int().min(0),
    totalPages: z.number().int().min(0)
  })
});


export const InvitationResponseSchema = z.object({
  invitationId: uuidSchema,
  userId: integerIdSchema,
  email: emailSchema,
  invitationToken: z.string().min(1).max(100).trim(),
  expiresAt: dateSchema,
  sentAt: dateSchema
});
// Inferred types
export type ContactDetailResponse = z.infer<typeof ContactDetailResponseSchema>;
export type ContactIdParam = z.infer<typeof ContactIdParamSchema>;
export type ContactResponse = z.infer<typeof ContactResponseSchema>;
export type ContactSearchQuery = z.infer<typeof ContactSearchQuerySchema>;
export type ContactSearchResponse = z.infer<typeof ContactSearchResponseSchema>;
export type ContactSearchResponseData = z.infer<typeof ContactSearchResponseSchema.shape.data>;
export type ContactSearchResult = z.infer<typeof ContactSearchResultSchema>;
export type ContactVersionHistoryItem = z.infer<typeof ContactVersionHistoryItemSchema>;
export type CreateContactRequest = z.infer<typeof CreateContactRequestSchema>;
export type CreateContactResponse = z.infer<typeof CreateContactResponseSchema>;
export type CreateContactResponseData = z.infer<typeof CreateContactResponseSchema.shape.data>;
export type CreateCustomerRequest = z.infer<typeof CreateCustomerRequestSchema>;
export type CreateCustomerResponse = z.infer<typeof CreateCustomerResponseSchema>;
export type CreateCustomerResponseData = z.infer<typeof CreateCustomerResponseSchema.shape.data>;
export type CreateGroupRequest = z.infer<typeof CreateGroupRequestSchema>;
export type CreateTenantUserRequest = z.infer<typeof CreateTenantUserRequestSchema>;
export type CustomerResponse = z.infer<typeof CustomerResponseSchema>;
export type GetContactByIdResponse = z.infer<typeof GetContactByIdResponseSchema>;
export type GetContactByIdResponseData = z.infer<typeof GetContactByIdResponseSchema.shape.data>;
export type GetContactDetailResponse = z.infer<typeof GetContactDetailResponseSchema>;
export type GetContactDetailResponseData = z.infer<typeof GetContactDetailResponseSchema.shape.data>;
export type GetContactsRequest = z.infer<typeof GetContactsRequestSchema>;
export type GetContactsResponse = z.infer<typeof GetContactsResponseSchema>;
export type GetContactsResponseData = z.infer<typeof GetContactsResponseSchema.shape.data>;
export type GetCustomerByIdResponse = z.infer<typeof GetCustomerByIdResponseSchema>;
export type GetCustomerByIdResponseData = z.infer<typeof GetCustomerByIdResponseSchema.shape.data>;
export type GetCustomerResponse = z.infer<typeof GetCustomerResponseSchema>;
export type GetCustomerResponseData = z.infer<typeof GetCustomerResponseSchema.shape.data>;
export type GetCustomersRequest = z.infer<typeof GetCustomersRequestSchema>;
export type GetGroupResponse = z.infer<typeof GetGroupResponseSchema>;
export type GetGroupResponseData = z.infer<typeof GetGroupResponseSchema.shape.data>;
export type GetGroupsRequest = z.infer<typeof GetGroupsRequestSchema>;
export type GetTenantUsersRequest = z.infer<typeof GetTenantUsersRequestSchema>;
export type GetUserResponse = z.infer<typeof GetUserResponseSchema>;
export type GroupResponse = z.infer<typeof GroupResponseSchema>;
export type GroupSummary = z.infer<typeof GroupSummarySchema>;
export type InvitationResponse = z.infer<typeof InvitationResponseSchema>;
export type ResendProviderInvitationResponse = z.infer<typeof ResendProviderInvitationResponseSchema>;
export type TenantGroup = z.infer<typeof GroupResponseSchema.shape.data>;
export type UpdateContactRequest = z.infer<typeof UpdateContactRequestSchema>;
export type UpdateContactResponse = z.infer<typeof UpdateContactResponseSchema>;
export type UpdateContactResponseData = z.infer<typeof UpdateContactResponseSchema.shape.data>;
export type UpdateCustomerRequest = z.infer<typeof UpdateCustomerRequestSchema>;
export type UpdateCustomerResponse = z.infer<typeof UpdateCustomerResponseSchema>;
export type UpdateCustomerResponseData = z.infer<typeof UpdateCustomerResponseSchema.shape.data>;
export type UpdateGroupRequest = z.infer<typeof UpdateGroupRequestSchema>;
export type UpdateTenantUserRequest = z.infer<typeof UpdateTenantUserRequestSchema>;
export type UserResponse = z.infer<typeof UserResponseSchema>;
export type UserSummary = z.infer<typeof UserSummarySchema>;
